/**
 * Case Canvas registries — the curated palette (Maltego-style transforms over our
 * case graph). DATA, not switch statements: adding an evidence type / operator /
 * algorithm is a row here.
 *
 * This file is the single source of truth. docs/research/canvas_bindings.md is
 * GENERATED from it (frontend/scripts/render-canvas-bindings.mjs); a test fails
 * when the two differ, so fix the ledger by regenerating, never by hand.
 *
 * `status` drives honesty: an operator is 'live' only when it starts a canvas job
 * (`jobOperator`) that calls an existing backend capability and writes its result
 * to the case spine. Anything else is 'wip' / 'new' / 'roadmap', carries an
 * `unavailable` reason and renders disabled (never a button that only posts a message).
 */
import {
  Banknote, FileArchive, Landmark, Wallet, Phone, AtSign, Hash, FileText, Instagram,
  Gavel, GitMerge, Radar, Search, Fingerprint, Share2, Link2, ScrollText, Package,
  ShieldCheck, Waypoints, Image as ImageIcon, Smartphone, Plus, StickyNote, Play, Crosshair, type LucideIcon,
} from 'lucide-react';

export type CanvasStatus = 'live' | 'wip' | 'new' | 'roadmap';
export type Tier = 'T1' | 'T2' | 'T3' | '—';

/** Typed input field for an evidence/operator card form (Intake Builder §3). */
export type InputField =
  | { key: string; kind: 'file'; label: string; accept: string; required?: boolean }
  | { key: string; kind: 'text' | 'textarea'; label: string; placeholder?: string; required?: boolean }
  | { key: string; kind: 'select'; label: string; options: { value: string; label: string }[] }
  | { key: string; kind: 'toggle'; label: string; default?: boolean }
  | { key: string; kind: 'number'; label: string; default?: number };

/** Entity/node types used across the canvas (match the fusion stylesheet shapes). */
export type NodeType =
  | 'wallet' | 'exchange' | 'account' | 'person' | 'company' | 'director'
  | 'handle' | 'ip' | 'identity_hub' | 'cluster' | 'vpa' | 'phone' | 'email' | 'document' | 'device';

export interface EvidenceType {
  id: string;                 // E1..E12
  name: string;
  icon: LucideIcon;
  desc: string;
  input: 'file' | 'paste' | 'json';
  status: CanvasStatus;
  tier: Tier;
  bloom: string;
  binding: string;            // human-readable route reference
  inputs?: InputField[];      // card form schema (Intake Builder); falls back to `input` when absent
  produces?: NodeType;        // for paste cards: the node type created (drives suggested-next-step chips)
}

export interface Operator {
  id: string;                 // O1..O15
  name: string;
  icon: LucideIcon;
  group: 'operate' | 'correlate' | 'generate';
  availableOn: NodeType[];
  desc: string;
  status: CanvasStatus;
  tier: Tier;
  binding: string;
  /**
   * 'case': runs on the whole case (its seeds and notes), so the palette starts it
   * without a selected node; `availableOn` only says where the radial menu offers it.
   * Default 'node'.
   */
  scope?: 'node' | 'case';
  /**
   * Canvas-job operator this entry starts via POST /canvas/{id}/jobs. Every LIVE
   * operator has one (asserted by bindingsLedger.test.ts and, on the backend, by
   * tests/test_canvas_bindings_ledger.py against the generated ledger).
   */
  jobOperator?: string;
  /** Why a non-LIVE operator cannot run today. Shown on the disabled button. */
  unavailable?: string;
}

export interface Algorithm { id: string; name: string; icon: LucideIcon; desc: string; status: CanvasStatus; binding: string; }
export interface Playbook { id: string; name: string; desc: string; steps: string[]; status: CanvasStatus; }

export const STATUS_META: Record<CanvasStatus, { label: string; color: string; live: boolean }> = {
  live:    { label: 'LIVE',          color: '#16a34a', live: true },
  wip:     { label: 'WIP',           color: '#d97706', live: false },
  new:     { label: 'BUILD',         color: '#7c5cfc', live: false },
  roadmap: { label: 'PILOT ROADMAP', color: '#94a3b8', live: false },
};

export const EVIDENCE: EvidenceType[] = [
  { id: 'E1', name: 'Bank Statement', icon: Banknote, input: 'file', status: 'live', tier: 'T1',
    desc: 'Parse a statement (StatementFlow) into account + counterparties', bloom: 'account + Dr/Cr edges',
    binding: 'bank-analyser /api/cases/{id}/statements',
    produces: 'account',
    inputs: [
      { key: 'file', kind: 'file', label: 'Statement file', accept: '.pdf,.xlsx,.xls,.csv,application/pdf,text/csv', required: true },
      { key: 'holder', kind: 'text', label: 'Account holder (optional)', placeholder: 'Name on the frozen account' },
      { key: 'password', kind: 'text', label: 'PDF password (if locked)', placeholder: 'DOB / account / phone digits' },
    ] },
  { id: 'E2', name: 'Exchange Subpoena', icon: FileArchive, input: 'file', status: 'live', tier: 'T1',
    desc: 'Ingest a Binance/exchange response (KYC + deposit wallets)', bloom: 'KYC person + wallets + edges',
    binding: 'POST /forensic/{case_id}/upload',
    produces: 'wallet',
    inputs: [
      { key: 'file', kind: 'file', label: 'Exchange / forensic PDF or archive', accept: '.pdf,.zip,.ufdr,.e01,.json,application/pdf,application/zip', required: true },
    ] },
  { id: 'E3', name: 'UFDR / E01 Archive', icon: FileArchive, input: 'file', status: 'live', tier: 'T1',
    desc: 'Extract wallets/VPAs/phones from a seized-device image', bloom: 'device + artefact nodes',
    binding: 'POST /forensic/{case_id}/upload (ufdr/e01 parser) · device_extract.v1 JSON',
    produces: 'wallet',
    inputs: [
      { key: 'file', kind: 'file', label: 'UFDR / E01 / device_extract JSON', accept: '.ufdr,.e01,.zip,.ab,.json,application/json', required: false },
      { key: 'value', kind: 'textarea', label: 'Or paste device_extract.v1 JSON', placeholder: '{ "schema": "device_extract.v1", … }' },
    ] },
  { id: 'E4', name: 'Wallet Address', icon: Wallet, input: 'paste', status: 'live', tier: 'T1',
    desc: 'Add a wallet (chain auto-detected)', bloom: 'wallet node', binding: 'client detect + entity create',
    produces: 'wallet', inputs: [{ key: 'value', kind: 'text', label: 'Wallet address', placeholder: 'T… / 0x…', required: true }] },
  { id: 'E6', name: 'VPA / UPI ID', icon: AtSign, input: 'paste', status: 'live', tier: 'T1',
    desc: 'Add a UPI VPA', bloom: 'VPA node', binding: 'entity create',
    produces: 'vpa', inputs: [{ key: 'value', kind: 'text', label: 'UPI VPA', placeholder: 'name@okhdfcbank', required: true }] },
  { id: 'E5', name: 'Phone Number', icon: Phone, input: 'paste', status: 'live', tier: 'T1',
    desc: 'Add a phone (lookup is an operator)', bloom: 'phone node', binding: 'entity create',
    produces: 'phone', inputs: [{ key: 'value', kind: 'text', label: 'Phone', placeholder: '+91…', required: true }] },
  { id: 'E7', name: 'Telegram Handle', icon: Hash, input: 'paste', status: 'live', tier: 'T1',
    desc: 'Add a Telegram handle', bloom: 'handle node', binding: 'entity create',
    produces: 'handle', inputs: [{ key: 'value', kind: 'text', label: 'Telegram handle', placeholder: '@handle', required: true }] },
  { id: 'E8', name: 'NCRP Complaint', icon: FileText, input: 'json', status: 'live', tier: 'T1',
    desc: 'Parse an NCRP complaint (victim + txns + beneficiary)', bloom: 'complaint + victim + txn edges',
    binding: 'ncrp_complaint.v1 client parser',
    produces: 'vpa',
    inputs: [
      { key: 'value', kind: 'textarea', label: 'NCRP complaint JSON', placeholder: '{ "schema": "ncrp_complaint.v1", … }', required: true },
      { key: 'file', kind: 'file', label: 'Or upload .json', accept: '.json,application/json' },
    ] },
  { id: 'E9', name: 'Instagram Handle', icon: Instagram, input: 'paste', status: 'live', tier: 'T3',
    desc: 'Add an Instagram handle node (OSINT enrichment stays on Instagram leads)', bloom: 'handle node',
    binding: 'entity create · Instagram OSINT desk for enrichment',
    produces: 'handle', inputs: [{ key: 'value', kind: 'text', label: 'Instagram handle', placeholder: '@handle', required: true }] },
  { id: 'E10', name: 'Generic Document', icon: FileText, input: 'file', status: 'wip', tier: 'T1',
    desc: 'Attach a document node to the case graph (locker seal WIP)', bloom: 'document node',
    binding: 'POST /cases/{id}/evidence (WIP) · client document node for now',
    produces: 'document',
    inputs: [
      { key: 'file', kind: 'file', label: 'Document', accept: '.pdf,.png,.jpg,.jpeg,.txt,.doc,.docx,application/pdf', required: true },
      { key: 'note', kind: 'text', label: 'Note (optional)', placeholder: 'Why this document matters' },
    ] },
  { id: 'E11', name: 'Image / Screenshot', icon: ImageIcon, input: 'file', status: 'live', tier: 'T1',
    desc: 'Upload an image → server-hashed to locker → OCR/QR extracts wallets/VPAs', bloom: 'artefact nodes + sealed image',
    binding: 'POST /intake/image (server SHA-256 → locker → OCR/QR)',
    inputs: [
      { key: 'file', kind: 'file', label: 'Image / screenshot', accept: 'image/*', required: true },
      { key: 'ocr', kind: 'toggle', label: 'OCR (text → addresses / VPAs / UTRs)', default: true },
      { key: 'qr', kind: 'toggle', label: 'QR decode (VPA / wallet)', default: true },
      { key: 'vision', kind: 'toggle', label: 'Vision LLM (describe / classify) — off by default', default: false },
      { key: 'cloud_vision', kind: 'toggle', label: 'Allow cloud vision for this image (applies only if an admin has switched it on)', default: true },
      { key: 'note', kind: 'text', label: 'Note (optional)', placeholder: 'e.g. WhatsApp chat with the OTC seller' },
    ] },
  { id: 'E12', name: 'Case Note', icon: StickyNote, input: 'paste', status: 'live', tier: 'T1',
    desc: 'Freeform text the officer asserts (complaint summary, tip, call note)', bloom: 'document / note node',
    binding: 'client document node + POST /canvas/{id}/nodes',
    produces: 'document',
    inputs: [
      { key: 'title', kind: 'text', label: 'Title', placeholder: 'e.g. Victim call — 11 Sep', required: true },
      { key: 'body', kind: 'textarea', label: 'Note', placeholder: 'Freeform text…', required: true },
    ] },
];

export const OPERATORS: Operator[] = [
  { id: 'O1', name: 'UTR-Stitch', icon: GitMerge, group: 'operate', availableOn: ['account'], tier: 'T1', status: 'live',
    jobOperator: 'utr_stitch',
    desc: 'Join Dr/Cr legs sharing a UTR into deterministic PAID edges', binding: 'POST /canvas/{id}/jobs (utr_stitch) → case_edges' },
  { id: 'O2', name: 'Off-Ramp Match', icon: Landmark, group: 'operate', availableOn: ['account', 'vpa'], tier: 'T1', status: 'live',
    jobOperator: 'offramp_match',
    desc: 'Name the exchange/OTC behind a beneficiary', binding: 'POST /canvas/{id}/jobs (offramp_match) → case_edges' },
  { id: 'O3', name: 'Auto-Investigate', icon: Radar, group: 'operate', availableOn: ['wallet'], tier: 'T1', status: 'live',
    jobOperator: 'auto_investigate',
    desc: 'Promote investigation summary (subject + endpoints) onto the case graph', binding: 'POST /canvas/{id}/jobs (auto_investigate)' },
  { id: 'O4', name: 'Triage', icon: Crosshair, group: 'operate', availableOn: ['wallet', 'exchange'], tier: 'T1', status: 'live',
    jobOperator: 'triage',
    desc: 'Workbench 7-way triage of this wallet: risk, entity, sanctions, P2P, OSINT mentions, nearest exchange',
    binding: 'POST /canvas/{id}/jobs (triage) → address triage service → case_entities + exposure edge · GET /chains/detect when the chain is ambiguous' },
  { id: 'O5', name: 'Identity Lookup', icon: Fingerprint, group: 'correlate', availableOn: ['phone', 'vpa', 'email'], tier: 'T2', status: 'live',
    jobOperator: 'identity_lookup',
    desc: 'Look up a phone / UPI ID / e-mail in the MuleHunter index and the cached identity pivots (no paid call)',
    binding: 'POST /canvas/{id}/jobs (identity_lookup) → MuleHunter screen + identity pivots → case_entities + affiliation edges' },
  { id: 'O6', name: 'Correlate ▸ P2P / Telegram', icon: Share2, group: 'correlate', availableOn: ['wallet', 'vpa', 'handle'], tier: 'T3', status: 'wip',
    desc: 'Find P2P ads + Telegram mentions of this identifier', binding: 'GET /p2p/address/{addr}/presence',
    unavailable: 'The P2P presence route reads only; nothing writes its result to the case graph yet' },
  { id: 'O7', name: 'Correlate ▸ UPI ↔ Wallet', icon: Link2, group: 'correlate', availableOn: ['vpa', 'wallet'], tier: 'T2', status: 'wip',
    desc: 'Bridge a VPA to linked wallets (and back)', binding: 'GET /p2p/upi/{upi}',
    unavailable: 'The UPI ↔ wallet routes read only; nothing writes their result to the case graph yet' },
  { id: 'O8', name: 'Cross-Case Link', icon: Search, group: 'correlate', availableOn: ['wallet', 'vpa', 'phone', 'account'], tier: 'T1', status: 'new',
    desc: 'Find this identifier in other cases you can access', binding: 'case-scoped entity search (to build)',
    unavailable: 'No cross-case identifier search exists yet (it needs case-visibility scoping)' },
  { id: 'O9', name: 'Generate ▸ 91/94 Notice', icon: Gavel, group: 'generate', availableOn: ['exchange'], tier: '—', status: 'live',
    jobOperator: 'notice',
    desc: 'Draft a Section 91/94 notice for this exchange (template registry); stored in the evidence locker',
    binding: 'POST /canvas/{id}/jobs (notice) → notice template registry → case_evidence + document node + subpoena edge' },
  { id: 'O10', name: 'Generate ▸ Case Package', icon: Package, group: 'generate', availableOn: ['identity_hub'], tier: '—', status: 'wip',
    desc: 'Export the case package (honours Court-Safe)', binding: 'GET /cases/{id}/export',
    unavailable: 'The export route downloads a file; it does not write to the case graph, so it is not a canvas operator yet' },
  { id: 'O11', name: 'Seal to Locker', icon: ScrollText, group: 'generate', availableOn: ['document'], tier: '—', status: 'live',
    jobOperator: 'seal',
    desc: 'Hash-seal this document’s content (SHA-256, computed server-side) into the evidence locker',
    binding: 'POST /canvas/{id}/jobs (seal) → case_evidence (hash_sha256) → document node marked sealed' },
  { id: 'O12', name: 'Auto-Investigate ▸ Case', icon: Play, group: 'operate', availableOn: ['wallet', 'identity_hub'], tier: 'T1', status: 'live',
    scope: 'case', jobOperator: 'case_worker',
    desc: 'Run the deep case worker over all case seeds and notes as a background job (recorded under Runs)',
    binding: 'POST /canvas/{id}/jobs (case_worker) → case_worker_run event · GET /canvas/{id}/jobs/{job_id}' },
  { id: 'O13', name: 'Trace ▸ Back / Forward', icon: Waypoints, group: 'operate', availableOn: ['wallet'], tier: 'T1', status: 'wip',
    desc: 'BFS hop trace to source / destination', binding: 'POST /investigation/backward-trace · /forward-trace',
    unavailable: 'The trace routes are not bound as a canvas job; the backward trace still times out (open P0)' },
  { id: 'O14', name: 'MuleHunter ▸ Screen Case', icon: ShieldCheck, group: 'correlate', availableOn: ['phone', 'vpa', 'wallet', 'handle', 'identity_hub'], tier: 'T3', status: 'live',
    scope: 'case', jobOperator: 'mulehunter_scan',
    desc: 'Match every case identifier against the MuleHunter index; promote hits and co-occurrence leads (scored)',
    binding: 'POST /canvas/{id}/jobs (mulehunter_scan) → MuleHunter case bridge → case_entities + cross_link edges' },
  { id: 'O15', name: 'Instagram ▸ Enrich Case', icon: Instagram, group: 'correlate', availableOn: ['handle', 'identity_hub'], tier: 'T3', status: 'live',
    scope: 'case', jobOperator: 'instagram_promote',
    desc: 'Promote the case’s harvested Instagram profiles and artefacts onto the case graph (scored)',
    binding: 'POST /canvas/{id}/jobs (instagram_promote) → Instagram OSINT promote → case_entities + affiliation edges' },
];

export const ALGORITHMS: Algorithm[] = [
  { id: 'A1', name: 'Cross-Rail Fan-In', icon: Waypoints, status: 'new',
    desc: 'Do fiat + crypto victim chains converge on one sink?', binding: 'needs unified case graph (§CG)' },
  { id: 'A2', name: 'Off-Ramp Convergence', icon: Landmark, status: 'new',
    desc: 'Which VASPs are the exit, and how much reached them?', binding: 'needs unified case graph (§CG)' },
];

export const PLAYBOOKS: Playbook[] = [
  { id: 'frozen_account_triage', name: 'Frozen-Account Triage', status: 'new',
    desc: 'CFCFRMS hold → clean-vs-tainted → notices',
    steps: ['ingest.bank_statement', 'utr_stitch', 'offramp_match', 'algo.cross_rail_fanin', 'generate.notice_9194', 'generate.case_package'] },
  { id: 'ncrp_to_national_linkage', name: 'NCRP → National Linkage', status: 'new',
    desc: 'The five-act demo arc, end to end',
    steps: ['E8', 'E1', 'O1', 'O2', 'E2', 'O3', 'O6', 'A1', 'O8', 'O10'] },
];

/** Operators valid for a given node type — drives the radial menu + suggested-next-step chips. */
export function operatorsForType(type?: string): Operator[] {
  if (!type) return [];
  return OPERATORS.filter((o) => o.availableOn.includes(type as NodeType));
}

export const evidenceById = (id: string) => EVIDENCE.find((e) => e.id === id);
export const operatorById = (id: string) => OPERATORS.find((o) => o.id === id);

/** Guided Start (Intake Builder §1) — "Start this case from…" → a pre-wired board (first card open). */
export interface GuidedStart { id: string; label: string; sub: string; icon: LucideIcon; cards: string[]; }
export const GUIDED_STARTS: GuidedStart[] = [
  { id: 'ncrp', label: 'NCRP complaint', sub: 'Start from a complaint (victim + txns + beneficiary)', icon: FileText, cards: ['E8'] },
  { id: 'frozen', label: 'Frozen account', sub: 'Start from the frozen account’s bank statement', icon: Banknote, cards: ['E1'] },
  { id: 'device', label: 'Seized device', sub: 'Start from a UFDR/E01 archive or an image', icon: Smartphone, cards: ['E3'] },
  { id: 'wallet', label: 'Wallet address', sub: 'Start from a single wallet and trace out', icon: Wallet, cards: ['E4'] },
  { id: 'identifiers', label: 'Phone + Instagram', sub: 'Start from phone and social handle identifiers', icon: Phone, cards: ['E5', 'E9'] },
  { id: 'blank', label: 'Blank board', sub: 'Add evidence cards yourself', icon: Plus, cards: [] },
];
